import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useTranslation } from 'react-i18next'

import { usersApi } from '../../api/endpoints'
import { useFormatters } from '../../hooks/useFormatters'
import { ErrorState } from '../../components/common/States'
import type { UserRole } from '../../types/api'

const ROLES: UserRole[] = ['OWNER', 'ADMIN', 'EDITOR']

/**
 * Who can sign in.
 *
 * <p>Owner only, and the API enforces that too - this screen simply will not load for anyone
 * else. Accounts are disabled rather than deleted, so a booking confirmed last summer still
 * says who confirmed it.
 */
export function AdminUsersPage() {
  const { t } = useTranslation()
  const { dateTimeAtEstate } = useFormatters()
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<UserRole>('ADMIN')
  const [password, setPassword] = useState('')

  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: usersApi.list })

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const create = useMutation({
    mutationFn: () => usersApi.create({ email, fullName, role, password }),
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setEmail('')
      setFullName('')
      setPassword('')
    },
  })

  const setEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      usersApi.setEnabled(id, enabled),
    onSuccess: invalidate,
  })

  const canCreate =
    email.includes('@') && fullName.trim().length > 1 && password.length >= 8

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2">{t('admin.users')}</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpen(true)}>
          {t('admin.addUser')}
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.usersIntro')}
      </Typography>

      {users.isError && <ErrorState error={users.error} onRetry={() => void users.refetch()} />}

      <Stack spacing={1.5}>
        {(users.data ?? []).map((user) => (
          <Paper
            key={user.id}
            elevation={0}
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 3,
              opacity: user.enabled ? 1 : 0.55,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={1.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {user.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                  {user.lastLoginAt
                    ? ` · ${t('admin.lastSignedIn')} ${dateTimeAtEstate(user.lastLoginAt)}`
                    : ` · ${t('admin.neverSignedIn')}`}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={t(`admin.role${user.role}`)} />
                {!user.enabled && <Chip size="small" color="default" label={t('admin.disabled')} />}
                <Button
                  size="small"
                  color="inherit"
                  disabled={setEnabled.isPending}
                  onClick={() => setEnabled.mutate({ id: user.id, enabled: !user.enabled })}
                >
                  {user.enabled ? t('admin.disable') : t('admin.enable')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {setEnabled.isError && <Box sx={{ mt: 2 }}><ErrorState error={setEnabled.error} /></Box>}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.addUser')}</DialogTitle>
        <DialogContent>
          {create.isError && <ErrorState error={create.error} />}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label={t('admin.fullName')}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              size="small"
            />
            <TextField
              label={t('admin.email')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              size="small"
            />
            <TextField
              select
              label={t('admin.role')}
              value={role}
              onChange={(event) => setRole(event.target.value as UserRole)}
              size="small"
              helperText={t(`admin.role${role}Help`)}
            >
              {ROLES.map((option) => (
                <MenuItem key={option} value={option}>
                  {t(`admin.role${option}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('admin.password')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              size="small"
              // Shown, not masked. Whoever creates the account has to read it out to the
              // person it belongs to, and there is no invitation e-mail to do that for them.
              helperText={t('admin.passwordHelp')}
              error={password.length > 0 && password.length < 8}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.back')}</Button>
          <Button
            variant="contained"
            disabled={!canCreate || create.isPending}
            onClick={() => create.mutate()}
          >
            {t('admin.addUser')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
